// vessels_manager.jsx
// CRUD for the cruise fleet (vessels table): list + filter + add/edit/delete.
// Matches the live navela-vessels API: integer imo, category enum, cruise line
// by name (upserted) or id, PATCH partial update, 204 delete.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Ship, Plus, Search, Pencil, Trash2, RefreshCw, ImageOff, Anchor, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Modal, Field, inputClass, Button, Loading, EmptyState, ConfirmDialog, strOrNull } from './navela_ui';

const PAGE_SIZE = 25;
const CATEGORIES = ['cruise', 'cargo', 'tanker', 'passenger', 'ferry', 'other'];

const intOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};
const floatOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const EMPTY_FORM = {
  imo: '', name: '', cruise_line: '', category: 'cruise', mmsi: '', call_sign: '',
  length_m: '', beam_m: '', gross_tonnage: '', passenger_capacity: '', year_built: '',
  flag: '', photo_url: '',
};

function toForm(v) {
  return {
    imo: v.imo ?? '',
    name: v.name ?? '',
    cruise_line: v.cruise_line ?? '',
    category: v.category ?? 'cruise',
    mmsi: v.mmsi ?? '',
    call_sign: v.call_sign ?? '',
    length_m: v.length_m ?? '',
    beam_m: v.beam_m ?? '',
    gross_tonnage: v.gross_tonnage ?? '',
    passenger_capacity: v.passenger_capacity ?? '',
    year_built: v.year_built ?? '',
    flag: v.flag ?? '',
    photo_url: v.photo_url ?? '',
  };
}

function toPayload(form) {
  return {
    imo: intOrNull(form.imo),
    name: form.name.trim(),
    cruise_line: strOrNull(form.cruise_line),
    category: form.category || 'cruise',
    mmsi: intOrNull(form.mmsi),
    call_sign: strOrNull(form.call_sign),
    length_m: floatOrNull(form.length_m),
    beam_m: floatOrNull(form.beam_m),
    gross_tonnage: intOrNull(form.gross_tonnage),
    passenger_capacity: intOrNull(form.passenger_capacity),
    year_built: intOrNull(form.year_built),
    flag: form.flag ? form.flag.trim().toUpperCase() : null,
    photo_url: strOrNull(form.photo_url),
  };
}

function Thumb({ url, alt }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return (
      <div className="flex h-11 w-16 items-center justify-center rounded-md bg-slate-100 text-slate-300">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return <img src={url} alt={alt} onError={() => setBroken(true)} className="h-11 w-16 rounded-md object-cover ring-1 ring-slate-200" loading="lazy" />;
}

export default function VesselsManager() {
  const { fetchVessels, createVessel, updateVessel, deleteVessel, fetchCruiseLines, loading } = useNavela();

  const [vessels, setVessels] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [cruiseLineId, setCruiseLineId] = useState('');
  const [cruiseLines, setCruiseLines] = useState([]);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await fetchVessels({
        search: search || undefined,
        category: category || undefined,
        cruise_line_id: cruiseLineId || undefined,
        limit: PAGE_SIZE,
        offset: opts.offset ?? offset,
      });
      setVessels(data?.vessels ?? []);
      setTotal(data?.total_count ?? 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load vessels');
    }
  }, [fetchVessels, search, category, cruiseLineId, offset]);

  // Cruise-line picker options (once).
  useEffect(() => {
    fetchCruiseLines()
      .then((d) => setCruiseLines(d?.cruise_lines ?? []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load({ offset: 0 }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, cruiseLineId]);

  useEffect(() => {
    load({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditing({}); };
  const openEdit = (v) => { setForm(toForm(v)); setEditing(v); };
  const closeModal = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (intOrNull(form.imo) === null || intOrNull(form.imo) <= 0) { toast.error('A positive IMO number is required'); return; }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing && editing.id) {
        await updateVessel(editing.id, payload);
        toast.success('Vessel updated');
      } else {
        await createVessel(payload);
        toast.success('Vessel created');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteVessel(deleteTarget.id);
      toast.success('Vessel deleted');
      setDeleteTarget(null);
      const nextOffset = vessels.length === 1 && offset >= PAGE_SIZE ? offset - PAGE_SIZE : offset;
      setOffset(nextOffset);
      load({ offset: nextOffset });
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const go = (nextOffset) => { setOffset(nextOffset); load({ offset: nextOffset }); };

  const specLine = (v) => {
    const bits = [];
    if (v.passenger_capacity) bits.push(`${Number(v.passenger_capacity).toLocaleString()} guests`);
    if (v.gross_tonnage) bits.push(`${Number(v.gross_tonnage).toLocaleString()} GT`);
    if (v.year_built) bits.push(`${v.year_built}`);
    return bits.join(' · ') || '—';
  };

  const isEmpty = !loading && vessels.length === 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Ship className="h-5 w-5 text-pr-navy-600" /> Vessels
          </h2>
          <p className="text-sm text-slate-500">{total.toLocaleString()} vessel{total === 1 ? '' : 's'} in the fleet</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => load()} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button onClick={openNew}><Plus className="h-4 w-4" /> Add vessel</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or IMO…" className={`${inputClass} pl-9`} />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputClass} w-40`}>
          <option value="">Any category</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
        </select>
        <select value={cruiseLineId} onChange={(e) => setCruiseLineId(e.target.value)} className={`${inputClass} w-52`}>
          <option value="">Any cruise line</option>
          {cruiseLines.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
        </select>
      </div>

      {loading && vessels.length === 0 ? (
        <Loading label="Loading vessels…" />
      ) : isEmpty ? (
        <EmptyState icon={Ship} title="No vessels match" hint="Adjust the filters, or add the first vessel to the fleet." action={<Button onClick={openNew}><Plus className="h-4 w-4" /> Add vessel</Button>} />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Vessel</th>
                  <th className="px-4 py-3">Cruise line</th>
                  <th className="px-4 py-3">IMO</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Specs</th>
                  <th className="px-4 py-3">Flag</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {vessels.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Thumb url={v.photo_url} alt={v.name} />
                        <div>
                          <div className="font-semibold text-slate-800">{v.name}</div>
                          <div className="text-xs text-slate-400">#{v.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{v.cruise_line || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{v.imo || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-pr-navy-50 px-2 py-0.5 text-xs font-semibold text-pr-navy-700">{v.category || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{specLine(v)}</td>
                    <td className="px-4 py-3 text-slate-500">{v.flag || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(v)} className="rounded-lg p-2 text-slate-400 hover:bg-pr-navy-50 hover:text-pr-navy-600" title="Edit"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteTarget(v)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span>Page {page} of {pageCount}</span>
            <div className="flex items-center gap-1">
              <button disabled={offset === 0} onClick={() => go(Math.max(0, offset - PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= pageCount} onClick={() => go(offset + PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={editing !== null}
        title={editing && editing.id ? 'Edit vessel' : 'Add vessel'}
        subtitle={editing && editing.id ? `#${editing.id} · ${editing.name}` : 'Create a new fleet vessel'}
        onClose={closeModal}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : editing && editing.id ? 'Save changes' : 'Create vessel'}</Button>
          </>
        }
      >
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="IMO number" required hint="7-digit IMO (numeric)">
            <input type="number" min="1" className={inputClass} value={form.imo} onChange={(e) => setForm({ ...form, imo: e.target.value })} placeholder="9820375" />
          </Field>
          <Field label="Name" required>
            <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Icon of the Seas" />
          </Field>
          <Field label="Cruise line" hint="Matched or created by name">
            <input list="navela-cruise-lines" className={inputClass} value={form.cruise_line} onChange={(e) => setForm({ ...form, cruise_line: e.target.value })} placeholder="Royal Caribbean" />
            <datalist id="navela-cruise-lines">
              {cruiseLines.map((cl) => <option key={cl.id} value={cl.name} />)}
            </datalist>
          </Field>
          <Field label="Category">
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Photo URL" className="sm:col-span-2" hint="Hero photo (https). Upload a gallery under Vessel Images.">
            <input className={inputClass} value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="MMSI">
            <input type="number" className={inputClass} value={form.mmsi} onChange={(e) => setForm({ ...form, mmsi: e.target.value })} />
          </Field>
          <Field label="Call sign">
            <input className={inputClass} value={form.call_sign} onChange={(e) => setForm({ ...form, call_sign: e.target.value })} />
          </Field>
          <Field label="Passenger capacity">
            <input type="number" min="0" className={inputClass} value={form.passenger_capacity} onChange={(e) => setForm({ ...form, passenger_capacity: e.target.value })} />
          </Field>
          <Field label="Gross tonnage">
            <input type="number" min="0" className={inputClass} value={form.gross_tonnage} onChange={(e) => setForm({ ...form, gross_tonnage: e.target.value })} />
          </Field>
          <Field label="Length (m)">
            <input type="number" min="0" step="any" className={inputClass} value={form.length_m} onChange={(e) => setForm({ ...form, length_m: e.target.value })} />
          </Field>
          <Field label="Beam (m)">
            <input type="number" min="0" step="any" className={inputClass} value={form.beam_m} onChange={(e) => setForm({ ...form, beam_m: e.target.value })} />
          </Field>
          <Field label="Year built">
            <input type="number" min="1800" max="2100" className={inputClass} value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} />
          </Field>
          <Field label="Flag" hint="ISO country code (e.g. BS)">
            <input className={inputClass} maxLength={2} value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value.toUpperCase() })} placeholder="BS" />
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete vessel?"
        message={deleteTarget ? `“${deleteTarget.name}” and its routes and images will be permanently removed. This cannot be undone.` : ''}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
        <Anchor className="h-3.5 w-3.5" /> Deleting a vessel cascades to its ship routes and images.
      </p>
    </div>
  );
}
